// Centralized i18n configuration
// Single source of truth for all internationalization settings

export const I18N_CONFIG = {
  // Supported languages
  supportedLanguages: [
    "en",
    "fa",
    "ku",
    "ar",
    "fr",
    "bal",
    "mzn",
    "glk",
    "az",
  ] as const,

  // Default language
  defaultLanguage: "en" as const,

  // RTL languages
  rtlLanguages: ["fa", "ar", "ku", "bal", "mzn", "glk", "az"] as const,

  // Pages that have translations for each language
  translatedPages: {
    en: ["/", "/faq", "/contact", "/changelog", "/privacy", "/terms"], // English has all pages
    fa: ["/", "/faq"], // Persian has homepage and FAQ
    ku: ["/", "/faq"], // Sorani Kurdish has homepage and FAQ
    ar: ["/", "/faq"], // Arabic has homepage and FAQ
    fr: ["/"], // French has only homepage
    bal: [], // Balochi - no pages translated yet (planned for future release)
    mzn: [], // Mazandarani - no pages translated yet (planned for future release)
    glk: [], // Gilaki - no pages translated yet (planned for future release)
    az: ["/", "/faq"], // Azeri Turkic (Iranian Azerbaijani) has homepage and FAQ
  } as const,

  // Language-specific configurations
  languages: {
    en: {
      code: "en",
      name: "English",
      nativeName: "English",
      isRTL: false,
      urlPrefix: "", // No prefix for English
    },
    fa: {
      code: "fa",
      name: "Persian",
      nativeName: "فارسی",
      isRTL: true,
      urlPrefix: "fa",
    },
    ku: {
      code: "ku",
      name: "Sorani Kurdish",
      nativeName: "کوردی",
      isRTL: true,
      urlPrefix: "ku",
    },
    ar: {
      code: "ar",
      name: "Arabic",
      nativeName: "العربية",
      isRTL: true,
      urlPrefix: "ar",
    },
    fr: {
      code: "fr",
      name: "French",
      nativeName: "Français",
      isRTL: false,
      urlPrefix: "fr",
    },
    bal: {
      code: "bal",
      name: "Balochi",
      nativeName: "بلۏچی",
      isRTL: true,
      urlPrefix: "bal",
    },
    mzn: {
      code: "mzn",
      name: "Mazani",
      nativeName: "مازرونی",
      isRTL: true,
      urlPrefix: "mzn",
    },
    glk: {
      code: "glk",
      name: "Gilaki",
      nativeName: "گیلکی",
      isRTL: true,
      urlPrefix: "glk",
    },
    az: {
      code: "az",
      name: "Azeri Turkic",
      nativeName: "آذری",
      isRTL: true,
      urlPrefix: "az",
    },
  } as const,
} as const;

// Type definitions
export type SupportedLanguage = (typeof I18N_CONFIG.supportedLanguages)[number];
export type LanguageConfig = (typeof I18N_CONFIG.languages)[SupportedLanguage];
export type TranslatedPages =
  (typeof I18N_CONFIG.translatedPages)[SupportedLanguage];

// Helper functions
export const isPageTranslated = (
  path: string,
  language: SupportedLanguage
): boolean => {
  const translatedPages = I18N_CONFIG.translatedPages[language];
  return (translatedPages as readonly string[]).includes(path);
};

export const isRTL = (language: string): boolean => {
  return I18N_CONFIG.rtlLanguages.includes(language as any);
};

export const getLanguageConfig = (
  language: SupportedLanguage
): LanguageConfig => {
  return I18N_CONFIG.languages[language];
};

export const getLanguageFromPath = (pathname: string): SupportedLanguage => {
  // Check for language prefixes
  for (const lang of I18N_CONFIG.supportedLanguages) {
    const config = getLanguageConfig(lang);
    if (
      config &&
      config.urlPrefix &&
      pathname.startsWith(`/${config.urlPrefix}/`)
    ) {
      return lang;
    }
    if (config && config.urlPrefix && pathname === `/${config.urlPrefix}`) {
      return lang;
    }
  }
  return I18N_CONFIG.defaultLanguage;
};

export const getBasePath = (pathname: string): string => {
  // Remove language prefix from path
  for (const lang of I18N_CONFIG.supportedLanguages) {
    const config = getLanguageConfig(lang);
    if (
      config &&
      config.urlPrefix &&
      pathname.startsWith(`/${config.urlPrefix}/`)
    ) {
      return pathname.substring(config.urlPrefix.length + 1);
    }
    if (config && config.urlPrefix && pathname === `/${config.urlPrefix}`) {
      return "/";
    }
  }
  return pathname;
};

export const buildLanguageUrl = (
  pathname: string,
  language: SupportedLanguage
): string => {
  const basePath = getBasePath(pathname);
  const config = getLanguageConfig(language);

  if (language === I18N_CONFIG.defaultLanguage) {
    return basePath;
  }

  if (!config || !config.urlPrefix) {
    return basePath;
  }

  return basePath === "/"
    ? `/${config.urlPrefix}`
    : `/${config.urlPrefix}${basePath}`;
};

export const getAvailableLanguages = (currentPath: string) => {
  return I18N_CONFIG.supportedLanguages.map((langCode) => {
    const config = getLanguageConfig(langCode);
    const isTranslated = isPageTranslated(currentPath, langCode);

    return {
      code: config?.code || langCode,
      name: config?.name || langCode,
      nativeName: config?.nativeName || langCode,
      isAvailable: langCode === I18N_CONFIG.defaultLanguage || isTranslated,
    };
  });
};

// Legacy function for backward compatibility
export const isPersianTranslatedPage = (path: string): boolean => {
  return isPageTranslated(path, "fa");
};
